import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AppContext";
import { getOrder, listProducts } from "../lib/dataStore";
import { Printer, ArrowLeft } from "lucide-react";
import { getInvoiceNo, INDIAN_STATES, isWestBengalState } from "../lib/orderHelpers";
import InvoiceSheet from "../components/InvoiceSheet";

export default function PrintOrder() {
  const { id } = useParams();
  const { user, isAdmin, loading } = useAuth();
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [gstMode, setGstMode] = useState("inclusive");
  const [selectedState, setSelectedState] = useState("West Bengal");
  const [breakdownCgstSgst, setBreakdownCgstSgst] = useState(false);
  const [showLabelTotal, setShowLabelTotal] = useState(false);
  const [advancePaid, setAdvancePaid] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    getOrder(id).then((ord) => {
      setOrder(ord || null);
      if (ord) {
        const hasOrderGst = ord.isGst === true || ord.isGstOrder === true || Number(ord.tax || 0) > 0 || Number(ord.taxTotal || 0) > 0;
        const isExplicitNonGst =
          !hasOrderGst &&
          (ord.isNonGst === true || ord.nonGst === true || ord.isGstOrder === false || ord.gstType === "non-gst" || ord.gstMode === "none" || ord.gstMode === "non-gst");

        if (isExplicitNonGst) {
          setGstMode("none");
        } else if (ord.gstMode && ord.gstMode !== "none" && ord.gstMode !== "non-gst") {
          setGstMode(ord.gstMode);
        } else {
          // If items subtotal + tax = gross total, tax was added on top (exclusive)
          const hasSeparateTax = Number(ord.tax || ord.taxTotal || 0) > 0 &&
            Math.abs((Number(ord.subtotal || 0) + Number(ord.tax || ord.taxTotal || 0)) - Number(ord.grossSubtotal || ord.total || 0)) < 2;
          setGstMode(ord.priceIncludesGst === false || hasSeparateTax ? "exclusive" : "inclusive");
        }
        if (ord.state) setSelectedState(ord.state);
        if (ord.breakdownCgstSgst != null) setBreakdownCgstSgst(!!ord.breakdownCgstSgst);
        if (ord.advancePaid != null && ord.advancePaid !== "") {
          setAdvancePaid(String(ord.advancePaid));
        }
      }
    });
    listProducts().then((prods) => setProducts(prods || []));
  }, [id]);

  if (loading) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (!user || !isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <h1 className="font-serif text-3xl mb-3">Not authorised</h1>
        <p className="text-muted-foreground">Only the admin can view invoices and shipping labels.</p>
      </div>
    );
  }
  if (!order) return <div className="p-16 text-center text-muted-foreground">Order not found. It may have been deleted.</div>;

  const invoiceNo = getInvoiceNo(order);
  const isWb = isWestBengalState(selectedState);
  const isCodOrder =
    Number(order.codCharge || 0) > 0 ||
    order.paymentType === "COD" ||
    (typeof order.paymentMethod === "string" && order.paymentMethod.toUpperCase().includes("COD"));

  return (
    <div className="print-root bg-muted min-h-screen">
      {/* ─── Screen-only toolbar ─────────────────────────── */}
      <div className="no-print sticky top-0 z-10 bg-background border-b border-border shadow-xs">
        <div className="max-w-[210mm] mx-auto px-4 py-2.5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button data-testid="print-back" onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground font-medium">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="text-xs sm:text-sm font-semibold text-foreground">
              Invoice #{invoiceNo}
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-2.5">
            {/* State Selection Dropdown */}
            <div className="flex items-center gap-1.5 bg-muted p-1 rounded-lg border border-border text-xs">
              <span className="text-muted-foreground font-medium text-[11px] hidden sm:inline">State (Tax):</span>
              <select
                data-testid="print-state-select"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="bg-background text-xs font-semibold text-foreground px-2 py-1 rounded border border-border focus:outline-hidden cursor-pointer"
              >
                {INDIAN_STATES.map((st) => (
                  <option key={st} value={st}>
                    {st} {isWestBengalState(st) ? "(WB • CGST+SGST)" : "(IGST)"}
                  </option>
                ))}
              </select>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${
                isWb ? "bg-emerald-500/10 text-emerald-700" : "bg-blue-500/10 text-blue-700"
              }`}>
                {isWb ? "CGST+SGST" : "IGST"}
              </span>
            </div>

            {/* GST / Non-GST Mode Toggle */}
            <div className="flex items-center bg-muted p-0.5 rounded-lg border border-border text-xs">
              <span className="px-2 text-muted-foreground font-medium text-[11px] hidden sm:inline">Mode:</span>
              <button
                type="button"
                data-testid="toggle-gst-inclusive"
                onClick={() => setGstMode("inclusive")}
                className={`px-2.5 py-1 rounded-md font-medium transition ${
                  gstMode === "inclusive"
                    ? "bg-background shadow-xs text-foreground font-bold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Selling prices already include GST"
              >
                Incl. GST
              </button>
              <button
                type="button"
                data-testid="toggle-gst-exclusive"
                onClick={() => setGstMode("exclusive")}
                className={`px-2.5 py-1 rounded-md font-medium transition ${
                  gstMode === "exclusive"
                    ? "bg-background shadow-xs text-foreground font-bold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="GST added on top of base taxable price"
              >
                Excl. GST
              </button>
              <button
                type="button"
                data-testid="toggle-gst-none"
                onClick={() => setGstMode("none")}
                className={`px-2.5 py-1 rounded-md font-medium transition ${
                  gstMode === "none"
                    ? "bg-neutral-800 text-white shadow-xs font-bold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Non-GST Invoice: No tax mentioned, company GST omitted"
              >
                Non-GST
              </button>
            </div>

            {/* COD Advance Input for Manual Invoice */}
            {isCodOrder && (
              <div className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-lg text-xs">
                <span className="text-amber-900 dark:text-amber-200 font-semibold text-[11px] whitespace-nowrap">
                  Advance (₹):
                </span>
                <input
                  type="number"
                  min="0"
                  data-testid="print-advance-input"
                  value={advancePaid}
                  onChange={(e) => setAdvancePaid(e.target.value)}
                  placeholder="0"
                  className="w-16 bg-background text-foreground font-mono font-bold text-xs px-1.5 py-0.5 rounded border border-border outline-none focus:border-terracotta"
                  title="Manual advance amount collected upfront"
                />
              </div>
            )}

            <button
              data-testid="print-btn"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta text-primary-foreground text-xs sm:text-sm font-medium hover:opacity-90 transition shadow-xs"
            >
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* ─── A4 sheet (Fits strictly on 1 Page) ──────────────── */}
      <InvoiceSheet
        order={{
          ...order,
          state: selectedState,
          advancePaid: advancePaid !== "" ? Number(advancePaid) : order.advancePaid,
        }}
        products={products}
        gstMode={gstMode}
        breakdownCgstSgst={breakdownCgstSgst}
        includeTotalValueOnLabel={showLabelTotal}
      />

      {/* ─── Strict Print Styles (1 A4 Page Only) ─────────── */}
      <style>{`
        .a4-sheet {
          width: 210mm;
          height: 297mm;
          max-height: 297mm;
          margin: 16px auto;
          background: #ffffff;
          color: #0a0a0a;
          padding: 8mm 10mm;
          box-shadow: 0 4px 24px rgba(0,0,0,0.1);
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow: hidden;
        }
        .pr-section { padding: 0; }
        .invoice { flex: 0 0 auto; }
        .shipping-label { flex: 0 0 auto; margin-top: auto; }
        .cut-line {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          border-top: 2px dashed #737373;
          padding-top: 4px;
        }

        @page {
          size: A4 portrait;
          margin: 0mm;
        }

        @media print {
          /* Hide non-printable elements */
          header, footer, nav, .no-print, [data-component="header"], [data-component="footer"] {
            display: none !important;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            max-height: 297mm !important;
            background: #ffffff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            overflow: hidden !important;
          }

          .print-root {
            background: #ffffff !important;
            padding: 0 !important;
            margin: 0 !important;
            min-height: 0 !important;
          }

          .a4-sheet {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 8mm 10mm !important;
            width: 210mm !important;
            height: 297mm !important;
            max-height: 297mm !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            page-break-before: avoid !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
}

